/**
 * Utility functions for handling product images
 */

/**
 * Gets the appropriate image path for a product
 * @param productName - The name of the product
 * @param type - The type of product ('drink' or 'food')
 * @param imageUrl - Optional image URL from the API
 * @returns The image path to use
 */
export const getProductImagePath = (
  productName: string, 
  type: 'drink' | 'food', 
  imageUrl?: string | null
): string => {
  // First, try to find a specific image based on the product name
  const normalizedName = productName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  
  const folder = type === 'drink' ? 'tragos' : 'platos';
  const localImagePath = `/images/${folder}/${normalizedName}.png`;
  
  // Return local image path first (the onError handler will handle if it doesn't exist)
  return localImagePath;
};

/**
 * Gets the default image path for products
 * @returns The default image path
 */
export const getDefaultProductImage = (): string => {
  return '/images/default.png';
};

/**
 * Handles image error by trying fallback options
 * @param event - The error event from the img element
 * @param productName - The name of the product
 * @param type - The type of product ('drink' or 'food')
 * @param imageUrl - Optional image URL from the API
 */
export const handleImageError = (
  event: React.SyntheticEvent<HTMLImageElement, Event>,
  productName: string,
  type: 'drink' | 'food',
  imageUrl?: string | null
): void => {
  const currentSrc = event.currentTarget.src;
  
  // If we're currently trying the local image and it failed, try the API image
  if (currentSrc.includes('/images/tragos/') || currentSrc.includes('/images/platos/')) {
    if (imageUrl && imageUrl.trim() !== '') {
      event.currentTarget.src = imageUrl;
      return;
    }
  }
  
  // If we're trying the API image and it failed, or no API image exists, use default
  event.currentTarget.src = getDefaultProductImage();
};

/**
 * Gets the image source with proper fallback handling
 * @param productName - The name of the product
 * @param type - The type of product ('drink' or 'food')
 * @param imageUrl - Optional image URL from the API
 * @returns Object with src and onError handler
 */
export const getProductImageProps = (
  productName: string,
  type: 'drink' | 'food',
  imageUrl?: string | null
) => {
  return {
    src: getProductImagePath(productName, type, imageUrl),
    onError: (event: React.SyntheticEvent<HTMLImageElement, Event>) => 
      handleImageError(event, productName, type, imageUrl)
  };
};
